import { z } from "zod";
import { cognitoUuid } from "./validation-common";

// ─── Enums ───────────────────────────────────────────────────────────────────

export const MediaTypeSchema = z.enum([
  "video_answer",
  "id_front",
  "id_back",
  "photo",
]);
export type MediaType = z.infer<typeof MediaTypeSchema>;

export const MediaStatusSchema = z.enum([
  "pending",
  "processing",
  "ready",
  "failed",
]);
export type MediaStatus = z.infer<typeof MediaStatusSchema>;

// ─── Core Entity ─────────────────────────────────────────────────────────────

export const CandidateMediaSchema = z.object({
  mediaId: z.uuid(),
  candidateId: cognitoUuid,
  type: MediaTypeSchema,
  s3Key: z.string(),
  contentType: z.string(),
  fileSizeBytes: z.number().int().nonnegative(),
  status: MediaStatusSchema,
  applicationId: z.uuid().optional(),
  questionId: z.string().optional(),
  thumbnailKey: z.string().optional(),
  durationSeconds: z.number().nonnegative().optional(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
});
export type CandidateMedia = z.infer<typeof CandidateMediaSchema>;

// ─── Response Schemas ─────────────────────────────────────────────────────────

export const PresignedUploadResponseSchema = z.object({
  uploadUrl: z.string(),
  mediaId: z.uuid(),
  s3Key: z.string(),
});
export type PresignedUploadResponse = z.infer<
  typeof PresignedUploadResponseSchema
>;

export const CandidateMediaDownloadResponseSchema = z.object({
  media: CandidateMediaSchema,
  downloadUrl: z.string(),
});
export type CandidateMediaDownloadResponse = z.infer<
  typeof CandidateMediaDownloadResponseSchema
>;

export const ManagerMediaListResponseSchema = z.object({
  items: z.array(CandidateMediaSchema),
});
export type ManagerMediaListResponse = z.infer<
  typeof ManagerMediaListResponseSchema
>;

export const ManagerMediaDownloadResponseSchema = z.object({
  media: CandidateMediaSchema,
  downloadUrl: z.string(),
});
export type ManagerMediaDownloadResponse = z.infer<
  typeof ManagerMediaDownloadResponseSchema
>;

export const DeleteMediaResponseSchema = z.object({
  deleted: z.boolean(),
  mediaId: z.uuid(),
});
export type DeleteMediaResponse = z.infer<typeof DeleteMediaResponseSchema>;

export const ConfirmUploadResponseSchema = CandidateMediaSchema;
export type ConfirmUploadResponse = z.infer<typeof ConfirmUploadResponseSchema>;

// ─── Request Schemas ──────────────────────────────────────────────────────────

export const PresignedUploadRequestSchema = z.object({
  type: MediaTypeSchema,
  contentType: z.string(),
  fileSize: z.number().int().positive(),
  applicationId: z.uuid().optional(),
  questionId: z.string().optional(),
});
export type PresignedUploadRequest = z.infer<
  typeof PresignedUploadRequestSchema
>;
